import { useState, useEffect, useCallback } from "react";
import axios from "axios";

const PERMISSION_V2_ENFORCED =
  process.env.REACT_APP_PERMISSION_V2_ENFORCED !== "false";

export default function useAccess() {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const [token, setToken] = useState(() => localStorage.getItem("token"));

  const [currentUser, setCurrentUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("me") || "{}");
    } catch {
      return {};
    }
  });

  const [permissions, setPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);

  const parseEmpRoles = useCallback((roles) => {
    try {
      const parsed = typeof roles === "string" ? JSON.parse(roles) : roles;
      return Array.isArray(parsed) ? parsed.map(Number) : [];
    } catch {
      return [];
    }
  }, []);

  useEffect(() => {
    const syncSession = () => {
      setToken(localStorage.getItem("token"));
      try {
        setCurrentUser(JSON.parse(localStorage.getItem("me") || "{}"));
      } catch {
        setCurrentUser({});
      }
    };
    const onStorage = (event) => {
      if (!event.key || ["token", "me", "emp_roles"].includes(event.key)) {
        syncSession();
      }
    };

    window.addEventListener("storage", onStorage);
    window.addEventListener("authSessionChanged", syncSession);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("authSessionChanged", syncSession);
    };
  }, []);

  const loadPermissions = useCallback(async () => {
    if (!token) {
      setPermissions([]);
      setPermissionsLoaded(true);
      return;
    }

    setPermissionsLoaded(false);

    try {
      const { data } = await axios.get(`${BASE_URL}/api/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const roleIds = parseEmpRoles(currentUser?.emp_roles);
      const filtered = Array.isArray(roleIds)
        ? data.filter((p) => roleIds.includes(Number(p.role_id)))
        : [];

      setPermissions(filtered);
    } catch (err) {
      console.error("Failed to load permissions:", err);
      setPermissions([]);
    } finally {
      setPermissionsLoaded(true);
    }
  }, [BASE_URL, token, currentUser, parseEmpRoles]);

  useEffect(() => {
    loadPermissions();
    const refresh = () => loadPermissions();
    window.addEventListener("permissionsUpdated", refresh);
    return () => window.removeEventListener("permissionsUpdated", refresh);
  }, [loadPermissions]);

  const hasPermission = useCallback(
    (pageName, resourceName, action) => {
      const normalizedAction = String(action || "").toLowerCase();
      if (!["view", "edit", "delete"].includes(normalizedAction)) return false;

      const roleIds = parseEmpRoles(currentUser?.emp_roles);
      const isSuperAdmin = roleIds.includes(17);
      if (normalizedAction === "delete" && isSuperAdmin) return true;
      if (pageName === "PermissionManagerPage" && normalizedAction === "edit") {
        return isSuperAdmin;
      }
      if (!PERMISSION_V2_ENFORCED) return normalizedAction !== "delete";

      return permissions.some(
        (p) =>
          p.page_name === pageName &&
          p.resource_name === resourceName &&
          p.action === normalizedAction,
      );
    },
    [permissions, currentUser, parseEmpRoles]
  );

  const hasAnyPagePermission = useCallback(
    (pageName, action = "view") => {
      const normalizedAction = String(action).toLowerCase();
      const roleIds = parseEmpRoles(currentUser?.emp_roles);
      if (normalizedAction === "delete" && roleIds.includes(17)) return true;
      if (pageName === "PermissionManagerPage" && normalizedAction === "edit") {
        return roleIds.includes(17);
      }
      if (!PERMISSION_V2_ENFORCED) return normalizedAction !== "delete";
      return permissions.some(
        (permission) =>
          permission.page_name === pageName &&
          permission.action === normalizedAction,
      );
    },
    [permissions, currentUser, parseEmpRoles],
  );

  return {
    currentUser,
    permissions,
    permissionsLoaded,
    hasPermission,
    hasAnyPagePermission,
    permissionV2Enforced: PERMISSION_V2_ENFORCED,
    reloadPermissions: loadPermissions,
  };
}
